// src/services/userPrefsService.js
// Filepath: // src/services/userPrefsService.js
/* eslint-disable no-console */
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// Updated defaults to match your requested slideshow behavior:
// - Random order: OFF by default
// - Time per image: 1 minute by default
// - Loop videos: still false by default (from prior step)
export const DEFAULT_TV_PREFS = {
  // Gallery
  galleryRandom: false,
  galleryMinutes: 15, // allowed range 1..60

  // Video
  loopVideos: false,
};

function clampMinutes(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return DEFAULT_TV_PREFS.galleryMinutes;
  return Math.max(1, Math.min(60, Math.round(v)));
}

/**
 * Coerce any remote/legacy data into the simplified schema.
 * - Ensures booleans and valid minute range
 */
function coerceTvPrefs(remote = {}) {
  const out = {
    galleryRandom: Boolean(remote.galleryRandom ?? DEFAULT_TV_PREFS.galleryRandom),
    galleryMinutes: clampMinutes(remote.galleryMinutes ?? DEFAULT_TV_PREFS.galleryMinutes),
    loopVideos: Boolean(remote.loopVideos ?? DEFAULT_TV_PREFS.loopVideos),
  };
  return out;
}

/**
 * Fetch merged user TV prefs from Firestore.
 * Path: users/{uid}.tvPrefs
 */
export async function fetchUserTvPrefs(uid) {
  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  const remote = snap.exists() ? snap.data()?.tvPrefs || {} : {};
  return coerceTvPrefs(remote);
}

/**
 * Save user TV prefs to Firestore (merge).
 * Only call this when the user has changed something.
 */
export async function saveUserTvPrefs(uid, prefs) {
  const ref = doc(db, "users", uid);
  const clean = coerceTvPrefs(prefs);
  await setDoc(ref, { tvPrefs: clean }, { merge: true });
  return clean;
}

/**
 * Optional: listen for live changes if other parts of the UI want to react.
 * Returns unsubscribe().
 */
export function listenUserTvPrefs(uid, callback) {
  const ref = doc(db, "users", uid);
  return onSnapshot(ref, (snap) => {
    const remote = snap.exists() ? snap.data()?.tvPrefs || {} : {};
    callback(coerceTvPrefs(remote));
  });
}
